/**
 * Plan for splitting `qty` units of an order item onto a new shipment.
 *
 * mode "move":   qty is the whole line, so the existing order-item row is moved
 *                via POST shipments/{new}/relationships/order-items.
 * mode "divide": the row keeps quantity - qty, and a new row of qty is created
 *                and attached to the new shipment.
 *
 * Never move a row with OrderItem.update(id, { shipmentId }): `shipmentId` is not
 * an attribute of the OrderItem model, so devour drops it and the PATCH is a
 * silent no-op (prod bug 2026-10-15, order 114-0149573-4249844).
 */
export function splitPlan(item, qty) {
  const total = parseInt(item && item.quantity, 10);
  const n = parseInt(qty, 10);
  if (!total || !n || n < 1 || n > total) {
    return { valid: false };
  }
  if (n === total) {
    return {
      valid: true,
      mode: "move",
      relationship: [{ id: String(item.id), type: "order-items" }],
      remainderQty: 0,
    };
  }
  return { valid: true, mode: "divide", remainderQty: total - n };
}

/** Items the source shipment keeps after the split (for its new dims). */
export function remainingAfterSplit(orderItems, item, qty) {
  const plan = splitPlan(item, qty);
  if (!plan.valid) return orderItems || [];
  const rest = (orderItems || []).filter(
    (oi) => String(oi.id) !== String(item.id)
  );
  if (plan.mode === "divide")
    rest.push({ ...item, quantity: plan.remainderQty });
  return rest;
}

/** True when every moved/created row now belongs to the new shipment. */
export function splitLanded(newShipmentItems, expectedUnits) {
  const units = (newShipmentItems || []).reduce(
    (sum, oi) => sum + (parseInt(oi.quantity, 10) || 0),
    0
  );
  return units === parseInt(expectedUnits, 10);
}
